export interface HealthPoint {
  t: string; // ISO date (day bucket) or datetime (hour bucket)
  value: number;
}

export interface HealthMetricSeries {
  unit: string | null;
  agg: 'sum' | 'avg';
  points: HealthPoint[];
}

export interface HealthAdminResponse {
  user: number;
  bucket: 'day' | 'hour' | string;
  from: string;
  to: string;
  metrics: Record<string, HealthMetricSeries>;
}

export type HealthChartKind = 'bar' | 'line';

export interface HealthMetricMeta {
  label: string;
  kind: HealthChartKind;
  color: string; // distinct accent per metric so the charts read as a set
}

// Display metadata for the known metrics. Cumulative metrics render as bars,
// point-in-time measurements as lines. Unknown metrics fall back to a line.
export const METRIC_META: Record<string, HealthMetricMeta> = {
  steps: { label: 'Steps', kind: 'bar', color: '#52c41a' },
  activeEnergy: { label: 'Active Energy', kind: 'bar', color: '#fa8c16' },
  distanceWalking: { label: 'Walking + Running Distance', kind: 'bar', color: '#0ca678' },
  exerciseTime: { label: 'Exercise Minutes', kind: 'bar', color: '#f76707' },
  sleep: { label: 'Sleep', kind: 'bar', color: '#722ed1' },
  restingHeartRate: { label: 'Resting Heart Rate', kind: 'line', color: '#f5222d' },
  hrv: { label: 'Heart Rate Variability', kind: 'line', color: '#e64980' },
  oxygenSaturation: { label: 'Blood Oxygen', kind: 'line', color: '#13c2c2' },
  bodyMass: { label: 'Weight', kind: 'line', color: '#1890ff' },
};

const FALLBACK_COLOR = '#597ef7';

export const metricLabel = (metric: string): string => METRIC_META[metric]?.label ?? metric;
export const metricKind = (metric: string): HealthChartKind => METRIC_META[metric]?.kind ?? 'line';
export const metricColor = (metric: string): string => METRIC_META[metric]?.color ?? FALLBACK_COLOR;
